/**
 * AppShell — embeds the Flutter web app (app.saysomethingin.com) in an
 * iframe that fills the full viewport below the marketing header.
 *
 * Layout:
 *   ┌──────────────────────────────────┐
 *   │  Marketing Header  (73 px)       │
 *   ├──────────────────────────────────┤
 *   │                                  │
 *   │   Flutter web app  (iframe)      │  ← 100vh - 73px, full width
 *   │                                  │
 *   └──────────────────────────────────┘
 *
 * The app URL is read from VITE_APP_URL at build time so it can point at
 * the staging or production Flutter host without a code change.
 *
 * Ivan's hosting checklist (required before this works cross-origin):
 *   - Set on app.saysomethingin.com CloudFront:
 *       Content-Security-Policy: frame-ancestors 'self' https://www.saysomethingin.com https://www.stage.saysomethingin.com
 *       X-Frame-Options: ALLOWFROM https://www.saysomethingin.com
 */

import { useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Header } from '../components/Header.jsx';
import { markHasAccount } from '../account/hasAccount.js';

// Height must stay in sync with headerStyles.inner in Header.jsx:
//   logo 36px + padding-top 18px + padding-bottom 18px + border-bottom 1px = 73px
const HEADER_HEIGHT = 73;

// Fallback to staging app URL if the env var isn't set yet
const APP_URL = import.meta.env.VITE_APP_URL || 'https://app.stage.saysomethingin.com';

const TWEAK_DEFAULTS = {
  navWelshResources: 'Welsh Resources',
  navAboutUs: 'About us',
  navContact: 'Contact',
  startLearning: 'Start Learning',
};

export default function AppShell() {
  const iframeRef = useRef(null);
  const [searchParams] = useSearchParams();

  // If the URL has ?token=XXX&c=YYY (from post-payment redirect),
  // pass them into the iframe so the Flutter app auto-logs the user in.
  const token = searchParams.get('token');
  const course = searchParams.get('c');
  let iframeSrc = APP_URL;
  if (token) {
    iframeSrc = `${APP_URL}/onestep?token=${encodeURIComponent(token)}${course ? `&c=${encodeURIComponent(course)}` : ''}`;
  }

  // Keep the outer page itself from scrolling — Flutter handles its own scroll
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, []);

  // Anyone reaching the app embed (bookmark, post-payment ?token=
  // redirect, "you already have access" link) is treated as a known
  // account holder — the header CTA points here instead of /pay.
  useEffect(() => { markHasAccount(); }, []);

  return (
    <div style={styles.page}>
      <Header t={TWEAK_DEFAULTS} />
      <div style={styles.iframeWrapper}>
        <iframe
          ref={iframeRef}
          src={iframeSrc}
          title="SaySomethingin App"
          style={styles.iframe}
          allow="camera; microphone; autoplay; encrypted-media; fullscreen"
          // No sandbox — the app needs cookies, scripts, and popups for auth flows.
          // External links (password reset, vouchers, T&Cs) must use target="_top"
          // or target="_blank" inside the Flutter app (url_launcher config).
        />
      </div>
    </div>
  );
}

const styles = {
  page: {
    display: 'flex',
    flexDirection: 'column',
    height: '100vh',
    overflow: 'hidden',
  },
  iframeWrapper: {
    flex: 1,
    // Belt-and-braces: explicit height in case flex doesn't propagate into iframe
    height: `calc(100vh - ${HEADER_HEIGHT}px)`,
    overflow: 'hidden',
  },
  iframe: {
    display: 'block',
    width: '100%',
    height: '100%',
    border: 'none',
  },
};
